import { useQuery } from "@tanstack/react-query";
import { cn } from "@tcg/ui";
import { CheckCircle2, FileText, Printer, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { MoneyText, useYen } from "@/components/MoneyText";
import { call } from "@/lib/api";

import { ReportView } from "./ReportView";
import { terminalRows, varianceTone, type ReportData } from "./report";
import { printShiftReport } from "./useShift";

const TONE = {
  ok: "border-pos-ok bg-pos-ok/10 text-pos-ok",
  warn: "border-status-pending bg-status-pending/15",
  problem: "border-status-problem bg-background text-foreground",
} as const;

/**
 * After the close (design/05 §3.7): what should be in the drawer vs what was counted, the
 * difference in green / yellow / red with one sentence, the card-terminal totals to check
 * against each terminal's own slip, and the 日計表 — print (80 mm, bytes from the server) /
 * PDF (media-service, appears by itself when ready).
 */
export function CloseResult({
  shiftId,
  report,
  threshold,
  onDone,
}: {
  shiftId: string;
  report: ReportData;
  threshold: number;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const yen = useYen();
  const { api, printer } = usePos();
  const [print, setPrint] = useState<"idle" | "printing" | "printed" | "failed">("idle");
  const variance = report.cash.variance ?? 0;
  const tone = varianceTone(variance, threshold);
  const magnitude = yen(Math.abs(variance));
  const sentence =
    tone === "ok"
      ? t("shift.variance_none")
      : variance < 0
        ? t(tone === "problem" ? "shift.variance_short_approved" : "shift.variance_short", {
            amount: magnitude,
          })
        : t(tone === "problem" ? "shift.variance_over_approved" : "shift.variance_over", {
            amount: magnitude,
          });

  const pdf = useQuery({
    queryKey: ["pos-shifts", "detail", shiftId, "z-pdf"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/shifts/{shift_id}/z-report", {
          params: { path: { shift_id: shiftId }, query: { format: "pdf" } },
        }),
      ),
    // the PDF is made in the background: ask again until it is there (no manual refresh)
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && "status" in data && (data.status === "pending" || data.status === "none")
        ? 2_000
        : false;
    },
  });
  const pdfData = pdf.data && "status" in pdf.data && pdf.data.format === "pdf" ? pdf.data : null;

  const printZ = async () => {
    setPrint("printing");
    const ok = await printShiftReport({ api, printer, shiftId, kind: "z" });
    setPrint(ok ? "printed" : "failed");
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_27rem] gap-4 p-4">
      <section aria-labelledby="close-title" className="flex min-h-0 flex-col gap-4">
        <h1 id="close-title" className="flex items-center gap-2 text-2xl font-semibold">
          <CheckCircle2 aria-hidden className="size-8 text-pos-ok" />
          {t("shift.closed_title")}
        </h1>
        <dl className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-md border p-3">
            <dt className="text-sm text-muted-foreground">{t("shift.expected")}</dt>
            <dd>
              <MoneyText amount={report.cash.expected} size="xl" data-testid="close-expected" />
            </dd>
          </div>
          <div className="rounded-md border p-3">
            <dt className="text-sm text-muted-foreground">{t("shift.counted")}</dt>
            <dd>
              <MoneyText amount={report.cash.counted ?? 0} size="xl" data-testid="close-counted" />
            </dd>
          </div>
          <div className={cn("rounded-md border-2 p-3", TONE[tone])} data-tone={tone}>
            <dt className="text-sm">{t("shift.variance")}</dt>
            <dd
              className={cn(
                "text-3xl font-semibold tabular-nums",
                tone === "problem" && "text-pos-problem",
              )}
              data-testid="close-variance"
            >
              {variance < 0 ? `−${magnitude}` : variance > 0 ? `+${magnitude}` : magnitude}
            </dd>
          </div>
        </dl>
        <p
          role={tone === "ok" ? "status" : "alert"}
          className={cn("flex items-center gap-2 rounded-md border-2 p-3 text-lg", TONE[tone])}
          data-testid="variance-sentence"
        >
          {tone === "ok" ? (
            <CheckCircle2 aria-hidden className="size-6 shrink-0" />
          ) : (
            <TriangleAlert
              aria-hidden
              className={cn("size-6 shrink-0", tone === "problem" && "text-pos-problem")}
            />
          )}
          {sentence}
        </p>
        {terminalRows(report).length > 0 ? (
          <section aria-labelledby="edc-title" className="rounded-md border p-3">
            <h2 id="edc-title" className="font-semibold">
              {t("shift.edc_title")}
            </h2>
            <p className="text-sm text-muted-foreground">{t("shift.edc_help")}</p>
            <ul className="mt-2 flex flex-col gap-1">
              {terminalRows(report).map((row) => (
                <li
                  key={`${row.terminalId ?? ""}-${row.cardBrand ?? ""}`}
                  className="flex justify-between gap-2 text-lg"
                >
                  <span>
                    {row.terminalId ?? t("shift.edc_unknown")} ·{" "}
                    {t(`brand.${row.cardBrand ?? ""}`, { defaultValue: row.cardBrand ?? "" })}{" "}
                    {t("report.count", { count: row.count })}
                  </span>
                  <MoneyText amount={row.amount} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="mt-auto grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => void printZ()}
            disabled={print === "printing"}
            className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
          >
            <Printer aria-hidden />
            {print === "printing" ? t("app.saving") : t("shift.print_z")}
          </button>
          {pdfData?.status === "issued" && pdfData.url ? (
            <a
              href={pdfData.url}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-lg"
            >
              <FileText aria-hidden />
              {t("shift.pdf")}
            </a>
          ) : (
            <p className="flex min-h-pos-touch items-center justify-center gap-2 rounded-md border text-muted-foreground">
              <FileText aria-hidden />
              {pdfData?.status === "unsupported"
                ? t("shift.pdf_unsupported")
                : t("shift.pdf_pending")}
            </p>
          )}
          <button
            type="button"
            onClick={onDone}
            className="min-h-pos-touch rounded-md bg-primary text-lg font-semibold text-primary-foreground"
          >
            {t("shift.done")}
          </button>
        </div>
        <p aria-live="polite" className="text-sm">
          {print === "printed" ? t("shift.printed") : null}
          {print === "failed" ? (
            <span role="alert" className="text-pos-problem">
              {t("shift.print_failed")}
            </span>
          ) : null}
        </p>
      </section>
      <div
        className="min-h-0 overflow-y-auto"
        tabIndex={0}
        role="region"
        aria-label={t("report.title_z")}
      >
        <ReportView report={report} />
      </div>
    </div>
  );
}
